/**
 * API Configuration
 * Centralized configuration for backend API endpoints
 */

// Get backend URL: In Production immer gleiche Origin wie die Seite (app.ai-crawler.cloud → /api)
const getBackendUrl = () => {
  // Development: Vite-Proxy
  if (import.meta.env.DEV) {
    return '/api';
  }

  // Production: Relative URL → Browser nutzt aktuelle Origin (app.ai-crawler.cloud oder aicrawler.cloud)
  // So wird die API immer von der gleichen Domain gezogen wie die Seite.
  if (import.meta.env.PROD) {
    return '/api';
  }

  // Fallback (z.B. Build/SSR): VITE_BACKEND_URL oder app.ai-crawler.cloud
  const viteUrl = import.meta.env.VITE_BACKEND_URL;
  if (viteUrl && typeof viteUrl === 'string' && viteUrl.trim()) {
    const baseUrl = viteUrl.trim().replace(/\/api\/?$/, '');
    return `${baseUrl}/api`;
  }
  return 'https://app.ai-crawler.cloud/api';
};

// API Configuration
export const API_CONFIG = {
  // Base URL for all API calls
  BASE_URL: getBackendUrl(),
  
  // Individual service endpoints
  ENDPOINTS: {
    // Data endpoints
    JUGENDAMT: {
      EXTRACT: '/data/extract',
      EXTRACT_STREAM: '/data/extract-stream',
      EXTRACT_SIMPLE: '/data/extract-simple',
      EXTRACT_BATCH: '/data/extract-batch',
      AUTO_SEARCH: '/data/auto-search-extract',
      AUTO_SEARCH_STREAM: '/data/auto-search-stream',
      EXCEL_BATCH: '/data/excel-batch-processing',
      HEALTH: '/data/health',
      HISTORY: '/data/history',
      HISTORY_SESSION: (sessionId) => `/data/history/${sessionId}`,
      HISTORY_RESULTS: (sessionId) => `/data/history/${sessionId}/results`,
      STATS: '/data/stats',
      DELETE_SESSION: (sessionId) => `/data/history/${sessionId}`,
      CLEANUP: '/data/history/cleanup'
    },
    
    // Crawl endpoints
    CRAWL: {
      START: '/crawl/',
      STATUS: (crawlId) => `/crawl/${crawlId}`,
      LIST: '/crawl/',
      CURRENT_STATUS: '/crawl/status/current',
      ACTIVE_STATUS: '/crawl/status/active'
    },
    
    // General endpoints
    HEALTH: '/health',
    ROOT: '/'
  },
  
  // Request configuration
  REQUEST_CONFIG: {
    TIMEOUT: 300000, // 5 minutes for long-running extractions
    HEADERS: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  },
  
  // Streaming configuration
  STREAMING: {
    UPDATE_INTERVAL: 3000, // 3 seconds
    CONNECTION_TIMEOUT: 30000, // 30 seconds
    RETRY_ATTEMPTS: 3
  }
};

// Helper function to build full URL
export const buildApiUrl = (endpoint) => {
  if (endpoint.startsWith('http')) {
    return endpoint; // Already a full URL
  }
  
  const baseUrl = API_CONFIG.BASE_URL;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  return `${baseUrl}${cleanEndpoint}`;
};

// Helper function to get environment info
export const getEnvironmentInfo = () => {
  return {
    mode: import.meta.env.MODE,
    dev: import.meta.env.DEV,
    prod: import.meta.env.PROD,
    backendUrl: API_CONFIG.BASE_URL,
    viteBackendUrl: import.meta.env.VITE_BACKEND_URL
  };
};

// Debug function to log API configuration
export const logApiConfig = () => {
  if (import.meta.env.DEV) {
    console.log('🔧 API Configuration:', {
      baseUrl: API_CONFIG.BASE_URL,
      environment: getEnvironmentInfo(),
      endpoints: API_CONFIG.ENDPOINTS
    });
    
    // Log specific endpoint URLs
    console.log('🔗 Key Endpoints:');
    console.log(`  - Auto Search Stream: ${buildApiUrl(API_CONFIG.ENDPOINTS.JUGENDAMT.AUTO_SEARCH_STREAM)}`);
    console.log(`  - Auto Search: ${buildApiUrl(API_CONFIG.ENDPOINTS.JUGENDAMT.AUTO_SEARCH)}`);
    console.log(`  - Extract: ${buildApiUrl(API_CONFIG.ENDPOINTS.JUGENDAMT.EXTRACT)}`);
  }
};

export default API_CONFIG;
